<template>
  <div class="page">
    <link href="https://fonts.googleapis.com/css?family=Lato:400,700" rel="stylesheet" />
    <div class="sidebar">
      <div class="sidebar__header">
        <div class="connect-logo"></div>
      </div>
      <div class="sidebar__body" v-if="loading">
        <n-loading-spinner></n-loading-spinner>
      </div>
      <div class="sidebar__body" v-if="!loading && !isTokenValid">
        <div class="sidebar-title">Registration Error</div>
        <div class="sidebar-subtitle">Something has gone wrong!</div>

        <div>{{ tokenErrorMsg }}</div>
      </div>
      <div class="sidebar__body" v-if="!loading && isTokenValid">
        <div class="sidebar-title">Register</div>
        <div class="sidebar-subtitle">Enter your account details to complete your registration</div>
        <div class="form-wrapper">
          <div class="field">
            <label class="label">Email</label>
            <div class="control">
              <input class="input" type="email" v-model="email" placeholder="Enter your email address" disabled />
            </div>
          </div>

          <div class="field">
            <label class="label">First Name</label>
            <div class="control">
              <input
                class="input"
                type="text"
                v-model.trim="firstName"
                @blur="$v.firstName.$touch()"
                placeholder="Enter your first name"
              />
            </div>

            <p v-if="$v.firstName.$invalid && $v.firstName.$dirty" class="help is-danger">
              Please enter your first name
            </p>
          </div>

          <div class="field">
            <label class="label">Last Name</label>
            <div class="control">
              <input
                class="input"
                type="text"
                v-model.trim="lastName"
                @blur="$v.lastName.$touch()"
                placeholder="Enter your last name"
              />
            </div>

            <p v-if="$v.lastName.$invalid && $v.lastName.$dirty" class="help is-danger">Please enter your last name</p>
          </div>

          <div class="field">
            <label class="label">Password</label>
            <div class="control">
              <input
                class="input"
                type="password"
                v-model.trim="password"
                @blur="$v.password.$touch()"
                placeholder="Enter your password"
              />
            </div>

            <p v-if="$v.password.$invalid && $v.password.$dirty" class="help is-danger">Password not sufficient</p>

            <p class="help">Must be at least 8 characters long</p>
            <p class="help">Must contain a mixture of uppercase and lowercase letters</p>
            <p class="help">Must contain at least 1 number</p>
          </div>

          <div class="field">
            <label class="label">Confirm Password</label>
            <div class="control">
              <input
                class="input"
                v-model.trim="confirmPassword"
                type="password"
                @blur="$v.confirmPassword.$touch()"
                placeholder="Re-enter your password"
              />
            </div>

            <p v-if="$v.confirmPassword.$invalid && $v.confirmPassword.$dirty" class="help is-danger">
              Passwords do not match
            </p>
          </div>

          <n-button variant="primary" :disabled="$v.$invalid" @click="register"> Register</n-button>
        </div>
        <p class="help">
          By clicking this button, you agree to Netcall's <a
            href="https://www.netcall.com/privacy-statement"
            target="_blank"
            >Privacy Policy</a
          >
        </p>
      </div>
      <div class="sidebar__footer">
        <div class="netcall-logo"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { workspacesApi } from '@/workspaces/workspaces';
import { RegisterUserViewModel } from '@/workspaces/models/request/registerUserViewModel';
import authService from '@/authentication/authentication.service';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import jwt_decode from 'jwt-decode';
import { InviteToken } from '@/features/registration/models/inviteToken';
import { onBeforeMount, ref } from 'vue';
import { minLength, required, sameAs, maxLength } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { useRoute } from 'vue-router';

const containsLetters = (value: string) => /(.*[a-z].*)/.test(value) && /(.*[A-Z].*)/.test(value);
const containsNumbers = (value: string) => /\d/.test(value);

const email = ref('');
const firstName = ref('');
const lastName = ref('');
const password = ref('');
const confirmPassword = ref('');
const loading = ref(true);
const isTokenValid = ref(false);
const tokenErrorMsg = ref('');
const token = ref('');

const validations = {
  firstName: {
    required,
    maxLength: maxLength(64),
  },
  lastName: {
    required,
    maxLength: maxLength(64),
  },
  password: {
    required,
    minLength: minLength(8),
    containsLetters,
    containsNumbers,
  },
  confirmPassword: {
    required,
    sameAs: sameAs('password'),
  },
};

const $v = useVuelidate(validations, { firstName, lastName, password, confirmPassword });
const route = useRoute();

onBeforeMount(async () => {
  token.value = <string>route.params.token;
  const validateResponse = await workspacesApi.validateRegistrationToken(token.value);
  loading.value = false;
  if (validateResponse.isValid) {
    isTokenValid.value = true;
    tokenErrorMsg.value = '';
    email.value = validateResponse.email;
  } else {
    isTokenValid.value = false;
    tokenErrorMsg.value = validateResponse.errorMsg;
  }
});

async function register(): Promise<void> {
  const user: RegisterUserViewModel = {
    email: email.value,
    firstName: firstName.value,
    lastName: lastName.value,
    password: password.value,
    tokenString: token.value,
  };
  try {
    await workspacesApi.registerUser(user);
    const token: InviteToken = jwt_decode(user.tokenString);
    if (token.loginUrl) {
      window.location.assign(token.loginUrl);
    } else {
      authService.login();
    }
  } catch (err: any) {
    tokenErrorMsg.value = err.message;
  }
}
</script>
<style scoped lang="scss">
// the following css has been lifted from the signin b2c template
// with some modifications to override bulma styles where relevant

html,
body,
div,
span,
applet,
object,
iframe,
h1,
h2,
h3,
h4,
h5,
h6,
p,
blockquote,
pre,
a,
abbr,
acronym,
address,
big,
cite,
code,
del,
dfn,
em,
img,
ins,
kbd,
q,
s,
samp,
small,
strike,
strong,
sub,
sup,
tt,
var,
b,
u,
i,
center,
dl,
dt,
dd,
ol,
ul,
li,
fieldset,
form,
label,
legend,
table,
caption,
tbody,
tfoot,
thead,
tr,
th,
td,
article,
aside,
canvas,
details,
embed,
figure,
figcaption,
footer,
header,
hgroup,
menu,
nav,
output,
ruby,
section,
summary,
time,
mark,
audio,
video {
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 100%;
  font: inherit;
  vertical-align: baseline;
}

.page {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  box-sizing: border-box;
}

*,
*:before,
*:after {
  box-sizing: inherit;
}

.page {
  height: 100vh;
  background: url(./../../../assets/background.jpeg);
  background-size: cover;
}

.sidebar {
  height: 100vh;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 500px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  padding: 0 70px;
}

.sidebar__header {
  flex: 1 0 auto;
  display: flex;
  align-items: flex-start;
  padding-top: 40px;
}

.sidebar__body {
  flex: 2 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.sidebar-title {
  font-size: 30px;
  font-weight: 700;
  margin-bottom: 6px;
  color: #062b80;
}

.sidebar-subtitle {
  color: #141930;
  margin-bottom: 32px;
}

.sidebar-subtext {
  font-size: 11px;
  color: rgba(20, 25, 48, 0.8);
}

.sidebar__footer {
  flex: 1 0 auto;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding-bottom: 40px;
}

a {
  color: #062b80;
}

.connect-logo {
  width: 100%;
  height: 24px;
  background-size: contain;
  background-repeat: no-repeat;
  background-image: url(./../../../assets/liberty-connect-logo.png);
}

.netcall-logo {
  height: 19px;
  width: 86px;
  background-size: contain;
  background-repeat: no-repeat;
  background-image: url(./../../../assets/netcall-logo.png);
}

p.help {
  color: rgba(33, 37, 41, 0.8);
  font-size: 0.75rem;
  margin-top: 0.25rem;

  &.is-danger {
    color: #ff3860;
  }
}

button {
  width: 100%;
  font-family: 'Lato', sans-serif;
  font-size: 18px;
  font-weight: 700;
  margin-top: 40px;
}

.form-wrapper {
  width: 100%;
}

input {
  font-family: 'Lato', sans-serif;
  border: 1px solid #dedede;
  border-radius: 3px;
  padding: 8px 12px;
  font-size: small;
  height: unset;
}
</style>
